import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from '@/components/ui/Tooltip';
import { AnalyticsHubSummary } from '@/types/analytics';
import { formatCompactNumber, formatPercent, formatDelta } from '@/utils/formatNumber';
import { PLATFORMS } from '@/components/ui/PlatformSelect';

/**
 * AnalyticsHeroStats Component
 *
 * Five KPI cards showing key metrics with period-over-period comparison.
 * 1. Total Reach
 * 2. Total Engagement
 * 3. Avg Engagement Rate
 * 4. Posts Published
 * 5. Best Performer (with platform icon and caption)
 */

interface AnalyticsHeroStatsProps {
  summary: AnalyticsHubSummary;
  previousPeriodSummary: AnalyticsHubSummary;
  isLoading: boolean;
  onBestPerformerClick?: (channelInstanceId: number) => void;
}

export const AnalyticsHeroStats = ({
  summary,
  previousPeriodSummary,
  isLoading,
  onBestPerformerClick,
}: AnalyticsHeroStatsProps) => {
  const { t } = useTranslation('analytics');

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="bg-white rounded-lg border border-gray-200 p-4 h-24 animate-pulse"
          >
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
            <div className="h-8 bg-gray-200 rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  // Helper to render delta badge
  const DeltaBadge = ({ current, previous }: { current: number; previous: number }) => {
    const delta = formatDelta(current, previous);

    let colorClasses = 'text-gray-500 bg-gray-50';
    if (!delta.isZero) {
      colorClasses = delta.isPositive
        ? 'text-green-600 bg-green-50'
        : 'text-red-600 bg-red-50';
    }

    return (
      <Tooltip content={t('tip_vs_previous', { defaultValue: 'Compared to the previous period of the same length' })} side="bottom">
        <span className={`px-2 py-0.5 rounded-md text-[10px] sm:text-xs font-medium cursor-help ${colorClasses}`}>
          {delta.value}
        </span>
      </Tooltip>
    );
  };

  // Get platform info for best performer
  const bestPerformerPlatform = summary.bestPerformer
    ? PLATFORMS.find((p) => p.value === summary.bestPerformer?.platform)
    : null;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
      {/* Card 1: Total Reach / Impressions */}
      <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
        <div className="flex items-center gap-1 text-xs text-gray-500 uppercase tracking-wide mb-1">
          {summary.totalReach > 0
            ? t('stat_total_reach', { defaultValue: 'Total Reach' })
            : t('stat_total_impressions', { defaultValue: 'Total Impressions' })}
          <Tooltip content={summary.totalReach > 0
            ? t('tip_reach', { defaultValue: 'Unique accounts that saw your posts. Available for LinkedIn, Facebook, Instagram, TikTok, and Threads. YouTube and Pinterest use impressions instead.' })
            : t('tip_impressions', { defaultValue: 'Total times your posts were displayed. Shown when reach is not available for the selected platforms (e.g., YouTube, Pinterest, Bluesky).' })} side="bottom">
            <i className="hm hm-info text-gray-400 text-[10px] cursor-help" />
          </Tooltip>
        </div>
        <div className="text-lg sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">
          {formatCompactNumber(summary.totalReach > 0 ? summary.totalReach : summary.totalImpressions)}
        </div>
        <DeltaBadge
          current={summary.totalReach > 0 ? summary.totalReach : summary.totalImpressions}
          previous={previousPeriodSummary.totalReach > 0 ? previousPeriodSummary.totalReach : previousPeriodSummary.totalImpressions}
        />
      </div>

      {/* Card 2: Total Engagement */}
      <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
        <div className="flex items-center gap-1 text-xs text-gray-500 uppercase tracking-wide mb-1">
          {t('stat_total_engagement', { defaultValue: 'Total Engagement' })}
          <Tooltip content={t('tip_engagement', { defaultValue: 'Total likes, comments, shares, and saves across all posts' })} side="bottom">
            <i className="hm hm-info text-gray-400 text-[10px] cursor-help" />
          </Tooltip>
        </div>
        <div className="text-lg sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">
          {formatCompactNumber(summary.totalEngagement)}
        </div>
        <DeltaBadge
          current={summary.totalEngagement}
          previous={previousPeriodSummary.totalEngagement}
        />
      </div>

      {/* Card 3: Avg Engagement Rate */}
      <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
        <div className="flex items-center gap-1 text-xs text-gray-500 uppercase tracking-wide mb-1">
          {t('stat_avg_engagement_rate', { defaultValue: 'Avg Engagement Rate' })}
          <Tooltip content={t('tip_eng_rate', { defaultValue: 'Engagement divided by reach (or impressions when reach is unavailable). Higher is better — above 3% is great.' })} side="bottom">
            <i className="hm hm-info text-gray-400 text-[10px] cursor-help" />
          </Tooltip>
        </div>
        <div className="text-lg sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">
          {formatPercent(summary.avgEngagementRate)}
        </div>
        <DeltaBadge
          current={summary.avgEngagementRate}
          previous={previousPeriodSummary.avgEngagementRate}
        />
      </div>

      {/* Card 4: Posts Published */}
      <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
        <div className="flex items-center gap-1 text-xs text-gray-500 uppercase tracking-wide mb-1">
          {t('stat_posts_published', { defaultValue: 'Posts Published' })}
          <Tooltip content={t('tip_posts_published', { defaultValue: 'Number of posts published in the selected time period' })} side="bottom">
            <i className="hm hm-info text-gray-400 text-[10px] cursor-help" />
          </Tooltip>
        </div>
        <div className="text-lg sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">
          {formatCompactNumber(summary.postsCount)}
        </div>
        <DeltaBadge
          current={summary.postsCount}
          previous={previousPeriodSummary.postsCount}
        />
      </div>

      {/* Card 5: Best Performer */}
      <div
        className={`bg-white rounded-lg border border-gray-200 p-3 sm:p-4 ${
          summary.bestPerformer && onBestPerformerClick ? 'cursor-pointer hover:border-primary-300 hover:shadow-sm transition-all' : ''
        }`}
        onClick={() => {
          if (summary.bestPerformer && onBestPerformerClick) {
            onBestPerformerClick(summary.bestPerformer.channelInstanceId);
          }
        }}
      >
        <div className="flex items-center gap-1 text-xs text-gray-500 uppercase tracking-wide mb-1">
          {t('stat_best_performer', { defaultValue: 'Best Performer' })}
          <Tooltip content={t('tip_best_performer', { defaultValue: 'Post with the highest total engagement in this period' })} side="bottom">
            <i className="hm hm-info text-gray-400 text-[10px] cursor-help" />
          </Tooltip>
        </div>
        {summary.bestPerformer && bestPerformerPlatform ? (
          <div className="mt-2">
            <div className="flex items-center gap-2 mb-1">
              <span
                className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: bestPerformerPlatform.color }}
              >
                <FontAwesomeIcon
                  icon={bestPerformerPlatform.icon}
                  className="text-white text-[10px]"
                />
              </span>
              <span className="text-xs font-medium text-gray-700">
                {bestPerformerPlatform.label}
              </span>
            </div>
            <Tooltip content={summary.bestPerformer.postCaption} side="bottom">
              <p className="text-sm text-gray-600 line-clamp-2">
                {summary.bestPerformer.postCaption}
              </p>
            </Tooltip>
            <div className="text-xs text-gray-500 mt-1">
              {formatCompactNumber(summary.bestPerformer.engagement)} {t('engagement', { defaultValue: 'engagement' })}
            </div>
          </div>
        ) : (
          <div className="text-2xl font-bold text-gray-400 mt-2">—</div>
        )}
      </div>
    </div>
  );
};
